/**
 * My Space "Header details" — the ONE definition of the box's copy and bound.
 *
 * Owner ruling (Pete, 2026-09-11): *"in new order, Order Details section I want
 * a full width text input box starting at 4 lines deep called "Header details".
 * This will be where a user in MySpace can type in delivery address, email,
 * phone number, order rules etc and it will appear on the order (Print and PDF)
 * under the ORDER REQUEST title. Add faded grey text showing an example such as
 * a made up delivery address and contact details. When the user clicks in the
 * box the example text disappears and they can type normally. A tool tip should
 * explain the use."*
 *
 * 🔑 THE EXAMPLE IS A `placeholder` ATTRIBUTE AND NOTHING ELSE — never a value,
 * never a default, never stored. *"When the user clicks in the box the example
 * text disappears"* is exactly what a placeholder already does; seeding it as a
 * VALUE would print an invented address on the first real order of anyone who
 * did not clear it, and would contradict the owner's third decision that the
 * stored default starts BLANK.
 *
 * 🚫 EVERY NAME, ADDRESS, NUMBER AND DOMAIN BELOW IS INVENTED. The owner's own
 * example is confidential and must not appear anywhere in this repo. The domain
 * is under `.example`, which RFC 2606 reserves precisely so it can never be
 * registered, and the phone number is inside Ofcom's `07700 900xxx` drama
 * range, which is never allocated to a real subscriber. Replacing either with
 * something that "looks more realistic" would mean shipping a real person's
 * contact details as placeholder text.
 */

/**
 * The faded example shown in the empty box. Three lines, because the field is
 * four rows deep and an example that fills it leaves no visible room to type.
 */
export const HEADER_DETAILS_PLACEHOLDER = [
  'INVOICE ADDRESS: Northlight Pictures Ltd, 12 Harbour Street, Bristol, BS1 4QA',
  'DELIVERY ADDRESS: Northlight Pictures Ltd, SFX Dept, Unit 7 Riverside Works, Bristol, BS2 0XY',
  'CONTACT: Sam Reed, 07700 900123, sfx@northlightpictures.example',
].join('\n');

/**
 * The ⓘ tooltip. It has to answer three questions the label cannot: WHERE the
 * text appears, WHAT belongs in it, and — the one a user cannot guess — that it
 * becomes their standing default.
 */
export const HEADER_DETAILS_TOOLTIP =
  'Printed under the ORDER REQUEST title on the PDF and print view. '
  + 'Use it for your invoice and delivery addresses, contact details and any standing order rules. '
  + 'Saved as your default for future My Space orders.';

/**
 * Rows the textarea opens at. The owner first asked for *"starting at 4 lines
 * deep"*; on staging the three-line placeholder wrapped and was cut off at 4,
 * so he doubled it (2026-09-11, same day): *"make header detail height twice
 * as tall by default as the example text is not fully visible"*.
 */
export const HEADER_DETAILS_ROWS = 8;

/**
 * Maximum stored length.
 *
 * 🪞 A MIRROR of the server's `headerDetailsField` bound in
 * `server/schemas/orders.js`, and a CONVENIENCE, never a gate — the server
 * refuses 2,001 characters independently, with a 400. This exists so the
 * browser stops the user at the same point rather than letting them type a long
 * block and lose it to a refusal on submit. If one side moves, move both.
 */
export const HEADER_DETAILS_MAX_LENGTH = 2000;
